import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePushRegistrationLifecycle } from "../../../push/PushRegistrationLifecycleProvider";
import { getPushCapability } from "../../../services/pushCapability";
import {
  confirmPushTestAttempt,
  fetchPushTestAttempt,
  fetchPushTestNotificationAvailability,
  isPushTestNotificationUiEnabled,
  sendPushTestNotification,
} from "../../../services/pushTestNotificationsApi";

export const PUSH_TEST_CONFIRMATION_STORAGE_KEY =
  "reelitpro.notificationHealth.lastConfirmedAt.v2";
export const PUSH_TEST_WAIT_MS = 60000;
export const PUSH_TEST_ARM_TIMEOUT_MS = 30000;
export const PUSH_TEST_POLL_INTERVAL_MS = 5000;
export const PUSH_TEST_MAX_POLLS = 6;
export const PUSH_TEST_EXPECTED_TITLE = "ReelitPro test notification";
export const PUSH_TEST_EXPECTED_BODY =
  "Notifications are working on this device.";

function permissionState() {
  try {
    return getPushCapability().permissionState;
  } catch {
    return "unsupported";
  }
}

function readConfirmedAt() {
  try {
    const value = window.localStorage.getItem(
      PUSH_TEST_CONFIRMATION_STORAGE_KEY
    );
    return value && !Number.isNaN(Date.parse(value)) ? value : null;
  } catch {
    return null;
  }
}

function permissionKey(state) {
  if (state === "unsupported") return "notification_health_permission_unsupported";
  if (state === "granted") return "notification_health_permission_allowed";
  if (state === "denied") return "notification_health_permission_not_allowed";
  return "notification_health_permission_not_requested";
}

function deviceKey(registration) {
  if (registration.registrationConfigurationMismatch) {
    return "notification_health_device_configuration_mismatch";
  }
  if (registration.active) return "notification_health_device_active";
  if (registration.status === "registration_ready") {
    return "notification_health_device_not_registered";
  }
  return "notification_health_device_inactive";
}

function testStateKey(state) {
  return {
    not_tested: "notification_health_test_not_tested",
    ready: "notification_health_test_ready",
    armed: "notification_health_test_armed",
    sending: "notification_health_sending",
    waiting: "notification_health_test_waiting",
    confirming: "notification_health_test_confirming",
    confirmed: "notification_health_test_confirmed",
    timed_out: "notification_health_test_timed_out",
    failed: "notification_health_test_failed",
    needs_attention: "notification_health_test_needs_attention",
    rate_limited: "notification_health_test_rate_limited",
  }[state] || "notification_health_test_not_tested";
}

export default function NotificationHealthSection({
  pushEnabled,
  directMessageMode,
}) {
  const { t } = useTranslation();
  const registration = usePushRegistrationLifecycle();
  const uiEnabled = isPushTestNotificationUiEnabled();
  const [permission, setPermission] = useState(permissionState);
  const [featureAvailable, setFeatureAvailable] = useState(false);
  const [availabilityResolved, setAvailabilityResolved] = useState(false);
  const [confirmedAt, setConfirmedAt] = useState(readConfirmedAt);
  const [testState, setTestState] = useState("not_tested");
  const [attempt, setAttempt] = useState(null);
  const [askReceipt, setAskReceipt] = useState(false);
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);
  const [notice, setNotice] = useState("");
  const mountedRef = useRef(false);
  const armedRef = useRef(null);
  const armTimerRef = useRef(null);
  const waitTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const pollCountRef = useRef(0);
  const abortRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      window.clearTimeout(armTimerRef.current);
      window.clearTimeout(waitTimerRef.current);
      window.clearTimeout(pollTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const refresh = () => setPermission(permissionState());
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  useEffect(() => {
    if (!uiEnabled) return undefined;
    const controller = new AbortController();
    fetchPushTestNotificationAvailability({ signal: controller.signal })
      .then(setFeatureAvailable)
      .catch(() => setFeatureAvailable(false))
      .finally(() => {
        if (!controller.signal.aborted) setAvailabilityResolved(true);
      });
    return () => controller.abort();
  }, [uiEnabled]);

  const prerequisitesMet =
    uiEnabled &&
    availabilityResolved &&
    featureAvailable &&
    permission === "granted" &&
    registration.registrationEnabled &&
    registration.vapidConfigured &&
    registration.active &&
    !registration.registrationConfigurationMismatch &&
    Boolean(registration.currentDeviceReference) &&
    pushEnabled === true &&
    directMessageMode === "immediate";
  const busy = ["armed", "sending", "waiting", "confirming"].includes(testState);
  const eligible = prerequisitesMet && !busy;
  const displayState = useMemo(
    () =>
      testState === "not_tested"
        ? eligible
          ? "ready"
          : confirmedAt
          ? "confirmed"
          : "not_tested"
        : testState,
    [confirmedAt, eligible, testState]
  );

  const finishConfirmed = useCallback((timestamp) => {
    const confirmed = timestamp || new Date().toISOString();
    try {
      window.localStorage.setItem(
        PUSH_TEST_CONFIRMATION_STORAGE_KEY,
        confirmed
      );
    } catch {
      // Server confirmation remains authoritative for this page.
    }
    setConfirmedAt(confirmed);
    setAskReceipt(false);
    setShowTroubleshooting(false);
    setTestState("confirmed");
  }, []);

  const pollAttempt = useCallback(
    async (activeAttempt) => {
      if (
        !mountedRef.current ||
        !activeAttempt ||
        pollCountRef.current >= PUSH_TEST_MAX_POLLS
      ) {
        return;
      }
      pollCountRef.current += 1;
      try {
        const result = await fetchPushTestAttempt(
          activeAttempt.attemptId,
          activeAttempt.deviceReference
        );
        if (!mountedRef.current) return;
        if (result.status === "confirmed") {
          finishConfirmed(result.confirmedAt);
          return;
        }
        if (result.status === "expired") {
          setTestState("timed_out");
          setShowTroubleshooting(true);
          return;
        }
        if (result.status === "failed") {
          setTestState("failed");
          return;
        }
      } catch {
        // Bounded polling is advisory; the explicit receipt action still works.
      }
      if (pollCountRef.current < PUSH_TEST_MAX_POLLS) {
        pollTimerRef.current = window.setTimeout(
          () => pollAttempt(activeAttempt),
          PUSH_TEST_POLL_INTERVAL_MS
        );
      }
    },
    [finishConfirmed]
  );

  const armTest = useCallback(() => {
    if (!eligible || armedRef.current) return;
    const armed = {
      deviceReference: registration.currentDeviceReference,
      sent: false,
    };
    armedRef.current = armed;
    setAttempt(null);
    setAskReceipt(false);
    setShowTroubleshooting(false);
    setNotice("");
    setTestState("armed");
  }, [eligible, registration.currentDeviceReference]);

  useEffect(() => {
    if (testState !== "armed" || !armedRef.current) return undefined;
    const armed = armedRef.current;
    const sendWhenHidden = () => {
      if (
        document.visibilityState !== "hidden" ||
        armedRef.current !== armed ||
        armed.sent
      ) {
        return;
      }
      armed.sent = true;
      window.clearTimeout(armTimerRef.current);
      setTestState("sending");
      sendPushTestNotification(armed.deviceReference, { keepalive: true })
        .then((result) => {
          if (!mountedRef.current) return;
          const activeAttempt = {
            ...result,
            deviceReference: armed.deviceReference,
          };
          armedRef.current = null;
          setAttempt(activeAttempt);
          setTestState("waiting");
          pollCountRef.current = 0;
          pollAttempt(activeAttempt);
          waitTimerRef.current = window.setTimeout(() => {
            if (!mountedRef.current) return;
            setAskReceipt(false);
            setShowTroubleshooting(true);
            setTestState("timed_out");
          }, PUSH_TEST_WAIT_MS);
        })
        .catch((error) => {
          if (!mountedRef.current) return;
          armedRef.current = null;
          setTestState(
            error?.code === "test_notification_retry_later"
              ? "rate_limited"
              : "failed"
          );
          setNotice(
            error?.code === "test_notification_retry_later"
              ? "notification_health_retry_later"
              : "notification_health_request_failure"
          );
        });
    };
    document.addEventListener("visibilitychange", sendWhenHidden);
    armTimerRef.current = window.setTimeout(() => {
      if (armedRef.current !== armed || armed.sent) return;
      armedRef.current = null;
      setNotice("notification_health_background_timed_out");
      setTestState("not_tested");
    }, PUSH_TEST_ARM_TIMEOUT_MS);
    return () => {
      document.removeEventListener("visibilitychange", sendWhenHidden);
      window.clearTimeout(armTimerRef.current);
    };
  }, [pollAttempt, testState]);

  useEffect(() => {
    if (testState !== "waiting") return undefined;
    const askOnReturn = () => {
      if (document.visibilityState === "visible") setAskReceipt(true);
    };
    document.addEventListener("visibilitychange", askOnReturn);
    askOnReturn();
    return () =>
      document.removeEventListener("visibilitychange", askOnReturn);
  }, [testState]);

  const confirmReceipt = async () => {
    if (!attempt || testState !== "waiting") return;
    setTestState("confirming");
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const result = await confirmPushTestAttempt(
        attempt.attemptId,
        attempt.deviceReference,
        { signal: controller.signal }
      );
      if (!mountedRef.current || controller.signal.aborted) return;
      if (result.status === "confirmed") {
        window.clearTimeout(waitTimerRef.current);
        finishConfirmed(result.confirmedAt);
      } else if (result.status === "expired") {
        setTestState("timed_out");
        setShowTroubleshooting(true);
      } else {
        setTestState("failed");
      }
    } catch {
      if (!controller.signal.aborted && mountedRef.current) {
        setTestState("failed");
        setNotice("notification_health_request_failure");
      }
    }
  };

  if (!uiEnabled) return null;

  const directKey =
    directMessageMode === "immediate"
      ? "notification_health_direct_enabled"
      : directMessageMode === "in_app_only"
      ? "notification_health_direct_in_app_only"
      : "notification_health_direct_off";
  const formattedConfirmedAt = confirmedAt
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(confirmedAt))
    : "";

  return (
    <div className="rp-notification-health">
      <div className="rp-notification-health__intro">
        <h4>{t("notification_health_title")}</h4>
        <p>{t("notification_health_enable_explanation")}</p>
      </div>
      <dl className="rp-notification-health__states">
        <div>
          <dt>{t("notification_health_permission_label")}</dt>
          <dd>{t(permissionKey(permission))}</dd>
        </div>
        <div>
          <dt>{t("notification_health_device_label")}</dt>
          <dd>{t(deviceKey(registration))}</dd>
        </div>
        <div>
          <dt>{t("notification_health_direct_label")}</dt>
          <dd>{t(directKey)}</dd>
        </div>
        <div>
          <dt>{t("notification_health_background_test_label")}</dt>
          <dd>{t(testStateKey(displayState))}</dd>
        </div>
      </dl>
      <div className="rp-notification-health__test">
        <button
          type="button"
          className="rp-notification-preferences__secondary-button"
          disabled={!eligible}
          onClick={armTest}
        >
          {t("notification_health_start_background_test")}
        </button>
        {availabilityResolved && !featureAvailable ? (
          <p className="rp-notification-health__note">
            {t("notification_health_unavailable")}
          </p>
        ) : null}
        {notice ? (
          <p className="rp-notification-health__note" role="status">
            {t(notice)}
          </p>
        ) : null}
        {testState === "armed" ? (
          <div className="rp-notification-health__receipt" role="status">
            <strong>{t("notification_health_background_armed")}</strong>
            <p>{t("notification_health_background_instructions")}</p>
            <p>
              <strong>{PUSH_TEST_EXPECTED_TITLE}</strong>
              <br />
              {PUSH_TEST_EXPECTED_BODY}
            </p>
          </div>
        ) : null}
        {askReceipt && testState === "waiting" ? (
          <div className="rp-notification-health__receipt">
            <strong>{t("notification_health_did_receive")}</strong>
            <div className="rp-notification-health__actions">
              <button type="button" onClick={confirmReceipt}>
                {t("notification_health_yes_worked")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAskReceipt(false);
                  setShowTroubleshooting(true);
                  setTestState("needs_attention");
                }}
              >
                {t("notification_health_no_troubleshooting")}
              </button>
            </div>
          </div>
        ) : null}
        {confirmedAt && displayState === "confirmed" ? (
          <p className="rp-notification-health__confirmed" role="status">
            {t("notification_health_last_confirmed", {
              timestamp: formattedConfirmedAt,
            })}
          </p>
        ) : null}
        {showTroubleshooting ? (
          <div className="rp-notification-health__troubleshooting">
            <h5>{t("notification_health_troubleshooting_title")}</h5>
            <p>{t("notification_health_provider_explanation")}</p>
            <ol>
              <li>{t("notification_health_android_permission")}</li>
              <li>{t("notification_health_android_lock_screen")}</li>
              <li>{t("notification_health_android_background_data")}</li>
              <li>{t("notification_health_android_power_saving")}</li>
              <li>{t("notification_health_android_reopen")}</li>
              <li>{t("notification_health_android_no_force_stop")}</li>
            </ol>
          </div>
        ) : null}
      </div>
    </div>
  );
}
